import * as React from "react";
import type { ReactNode, JSX } from "react";

import styled from "styled-components";

import {
    Tabs,
    TabList,
    Tab,
    TrackerAssetChipItem,
    HomepageWebsiteCompareItem,
    HomepageWebsiteItem,
} from "@similarweb/ui-components";

import { AIReportCtaPlacement, AIReportCtaSection } from "app/components/ai-reports-cta-section/AIReportCtaSection";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { FeedbackSideTab } from "app/components/React/sideTabs";
import { RewardsSideBarContainer } from "app/components/React/sideTabs/RewardsSideBarContainer";
import { i18nFilter } from "app/filters/i18nFilter";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { AssetsService } from "app/services/AssetsService";
import { FavoritesService } from "app/services/favorites/favoritesService";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import {
    getRecentsAnalysis,
    getTransformedItem,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { AutocompleteWebsitesBase } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesBase";
import { PagePreview } from "pro-features/components/PagePreview/PagePreview";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { fetchSavedProperties, transformSavedProperties } from "../../research-homepage/pageResources";

import {
    TrackerAssetChipItemStyle,
    HomePage,
    ListExamplesWrapper,
    HomepageHeader,
    HomepageInnerWrapper,
    BodyWrapper,
    HomepageBody,
    TextPopularWebsites,
    TitlesHeader,
    HeaderContent,
    Title,
    SubTitle,
    AutocompleteWebsiteWrapper,
    EmptyStateText,
    TabsContainer,
    HomepagePreviewInnerWrapper,
    TitlePagePreview,
    TitlePagePrevieWrapper,
    LineTitle,
    HomePageBannerWrapper,
} from "./AnalysisHomePageTrialStyle";
import { pagesPreview } from "./Consts";
import { allTrackers } from "app/services/track/track";

const translate = i18nFilter();
const defaultWebsitePageState = "digitalsuite_website_websiteperformance";

interface ICompetitiveAnalysisHomepageItem {
    Id?: string;
    Type: string;
    Name: string;
    url: string;
    Icon: string;
    addedTime: string;
}

enum ETabs {
    RECENT,
    FAVORITES,
}

const onClickRedirect = (url: string, name): void => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `saved/website/${name.type || ""}`);
    window.location.href = url;
};

/**
 * Returns the item Id, or in case no such exists - create a unique id for the item
 * this is used as the item key, to avoid rendering issues in react.
 */
const resolveUniqueItemId = (item: ICompetitiveAnalysisHomepageItem, prefix: string): string => {
    const { Id, Type, Name, addedTime } = item;
    return Id ? Id : `${prefix}_type_${Type};Name_${Name};AddedTime_${addedTime}`;
};

const createHomepageItems = (
    items,
    removeFunc,
    onClick: (domain: string, competitiveAnalysisUrl: string) => void,
): JSX.Element[] => {
    return items.map((item: ICompetitiveAnalysisHomepageItem) => {
        return (
            <HomepageWebsiteItem
                key={resolveUniqueItemId(item, "favorites")}
                domain={item.Name}
                onItemClick={() => onClick(item.Name, item.url)}
                onFavoriteClick={removeFunc ? removeFunc.bind(null, item.Id) : null}
                iconSrc={item.Icon}
                isFavorite={true}
            />
        );
    });
};

const createHomePageRecentItem = (
    items = [],
    onClick: (domain: string, competitiveAnalysisUrl: string) => void,
    websitePageStateOverride?: string,
) => {
    return items.map((item, index) => {
        const { mainWebsite, compareItems, url } = getTransformedItem(
            item,
            websitePageStateOverride ?? defaultWebsitePageState,
        );
        if (Array.isArray(compareItems) && compareItems.length) {
            const key = [mainWebsite.domain].concat(compareItems.map(({ domain }) => domain)).join(",");
            return (
                <HomepageWebsiteCompareItem
                    key={resolveUniqueItemId(item, `recent_${index}`)}
                    mainItem={{ domain: mainWebsite.domain, iconSrc: mainWebsite.iconUrl }}
                    competitors={compareItems.map(({ domain, iconUrl }) => {
                        return {
                            domain,
                            iconSrc: iconUrl,
                        };
                    })}
                    isFavorite={false}
                    onFavoriteClick={null}
                    onItemClick={() => onClick(key, url)}
                />
            );
        }
        return (
            <HomepageWebsiteItem
                key={resolveUniqueItemId(item, `recent_${index}`)}
                domain={mainWebsite.domain}
                iconSrc={mainWebsite.iconUrl}
                isFavorite={false}
                onFavoriteClick={null}
                onItemClick={() => onClick(mainWebsite.domain, url)}
            />
        );
    });
};

const EmptyState = styled.div<{ width?: number }>`
    width: ${({ width }) => (width ? `${width}px` : "auto")};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 51px;

    img {
        margin-bottom: 66px;
    }
`;

export const AnalysisHomePageTrial: React.FC<{
    websitePageStateOverride?: string;
    placeholder?: ReactNode;
}> = ({ websitePageStateOverride, placeholder }) => {
    const popularSitesExamples = ["ebay.com", "amazon.com", "aliexpress.com"];
    const { data: popularSitesWithIcon = null } = useWebsitesFavicons(popularSitesExamples, undefined, {
        enabled: popularSitesExamples.length > 0,
    });
    const [loading, setLoading] = React.useState(true);
    const [favorites, setFavorites] = React.useState([]);
    const [recents, setRecents] = React.useState([]);
    const [selectedTab, setSelectedTab] = React.useState<ETabs>(ETabs.RECENT);
    const [maxScrollDown, setMaxScrollDown] = React.useState(0);
    const maxScrollDownRef = React.useRef(0);

    const removeFromFavorites = async (id: string): Promise<void> => {
        const newFavorites: any = await FavoritesService.removeFavoriteById({ id });

        const items = newFavorites?.items
            ? transformSavedProperties(
                  { items: newFavorites?.items.filter((item) => item.data.type === "website") },
                  websitePageStateOverride ?? defaultWebsitePageState,
              )
            : [];
        setFavorites(items);
    };

    React.useEffect(() => {
        const getFavoritesAndRecents = async (): Promise<void> => {
            const favoriteSitesPromis: any = fetchSavedProperties();
            const recentItemsPromise = getRecentsAnalysis("website", true);
            try {
                const [recentItems, favoriteSites] = await Promise.all([recentItemsPromise, favoriteSitesPromis]);
                const favoritesItemsTransformed = transformSavedProperties(
                    {
                        items: (favoriteSites?.payload?.items ?? []).filter((item) => item.data.type === "website"),
                    },
                    websitePageStateOverride ?? defaultWebsitePageState,
                );
                setFavorites(favoritesItemsTransformed);
                const recentItemsTransformed = (recentItems || []).slice(0, 10);
                setRecents(recentItemsTransformed);
                // if there are no recents, Set the active tab to be favorites
                if (recentItemsTransformed.length === 0 && favoritesItemsTransformed.length > 0) {
                    setSelectedTab(1);
                }
            } finally {
                setLoading(false);
            }
        };
        getFavoritesAndRecents();
        return () => {
            allTrackers.dispatchTrackEvent(
                "Scroll",
                "scroll",
                `scroll down in website analysis homepage/${maxScrollDownRef.current || ""}`,
            );
        };
    }, []);

    React.useEffect(() => {
        maxScrollDownRef.current = maxScrollDown;
    }, [maxScrollDown]);

    const renderListItems = () => {
        switch (selectedTab) {
            case ETabs.RECENT:
                if (recents.length === 0) {
                    return [
                        <EmptyState key="recent-empty-state">
                            <img
                                src={AssetsService.assetUrl("/images/empty-state-recent.svg")}
                                width={75}
                                style={{ minHeight: 70 }}
                                alt={"empty State"}
                            />
                            <EmptyStateText>
                                {i18nFilter()("aquisitionintelligence.competitiveanalysis.home.recent.emptystate")}
                            </EmptyStateText>
                        </EmptyState>,
                    ];
                }
                return createHomePageRecentItem(
                    recents,
                    (domain, url) =>
                        onClickRedirect(url, {
                            type: "recent",
                            key: domain,
                        }),
                    websitePageStateOverride,
                );
            case ETabs.FAVORITES:
                if (favorites.length === 0) {
                    return [
                        <EmptyState
                            key="favorites-empty-state"
                            width={320}
                        >
                            <img
                                src={AssetsService.assetUrl("/images/empty-state-favorites.svg")}
                                width={205}
                                alt={"empty State"}
                            />
                            <EmptyStateText>
                                {i18nFilter()("aquisitionintelligence.competitiveanalysis.home.favorites.emptystate")}
                            </EmptyStateText>
                        </EmptyState>,
                    ];
                }
                return createHomepageItems(favorites, removeFromFavorites, (domain, url) =>
                    onClickRedirect(url, {
                        type: "favorites",
                        key: domain,
                    }),
                );
        }
    };

    const onTabSelect = (tab: ETabs) => {
        setSelectedTab(tab);
    };

    const onPopularWebsiteClicked = (web) => {
        allTrackers.dispatchTrackEvent("Button", "click", `popular/website/${web || ""}`);
        proNavigator.go("digitalsuite_website_websiteperformance", {
            country: 999,
            duration: "3m",
            key: web,
            Source: "Total",
            isWWW: "*",
        });
    };

    const renderWebsiteExamples = () => {
        if (recents.length > 0 || loading) {
            return null;
        }
        return (
            <ListExamplesWrapper>
                <TextPopularWebsites>{i18nFilter()("homepage.Popular.websites")}</TextPopularWebsites>
                {popularSitesExamples.map((web) => (
                    <TrackerAssetChipItemStyle key={web}>
                        <TrackerAssetChipItem
                            onClick={() => {
                                onPopularWebsiteClicked(web);
                            }}
                            text={web}
                            image={popularSitesWithIcon?.[web] ?? ""}
                        />
                    </TrackerAssetChipItemStyle>
                ))}
            </ListExamplesWrapper>
        );
    };

    const renderBody = () => {
        if (loading) {
            return (
                <BodyWrapper>
                    <LoadingSpinner />
                </BodyWrapper>
            );
        }
        if (recents.length > 0) {
            return (
                <BodyWrapper>
                    <div>
                        <TabsContainer>
                            <Tabs
                                selectedIndex={selectedTab}
                                onSelect={onTabSelect}
                            >
                                <TabList>
                                    <Tab>
                                        {translate("aquisitionintelligence.competitiveanalysis.recent.listTitle")}
                                    </Tab>
                                    <Tab>{translate("aquisitionintelligence.competitiveanalysis.listTitle")}</Tab>
                                </TabList>
                            </Tabs>
                        </TabsContainer>
                    </div>
                    {renderListItems()}
                </BodyWrapper>
            );
        }
        return;
    };
    const onClickPagePreviewButton = (navigationState, pageName, params?) => {
        allTrackers.dispatchTrackEvent("Button", "click", `Education hub/Go to ${pageName || ""}`);
        const finalParams = params ?? proNavigator.getState(navigationState).defaultQueryParams;
        proNavigator.go(navigationState, finalParams);
    };

    const onScroll = (e) => {
        if (e.target.scrollHeight - e.target.clientHeight > 0) {
            const scrollRatio = e.target.scrollTop / (e.target.scrollHeight - e.target.clientHeight);
            const scrollPercentage = Math.round(scrollRatio * 100);

            if (scrollPercentage > maxScrollDown) {
                setMaxScrollDown(scrollPercentage);
            }
        }
    };
    return (
        <ScrollContainer onScroll={onScroll}>
            <HomePage>
                <HomepageHeader>
                    <HomepageInnerWrapper>
                        <TitlesHeader>
                            <Title>{i18nFilter()("websites.home.pageTitle")}</Title>
                            <SubTitle>{i18nFilter()("websites.home.page.subtitle.line1")}</SubTitle>
                            <SubTitle>{i18nFilter()("websites.home.page.subtitle.line2")}</SubTitle>
                        </TitlesHeader>
                        <HeaderContent>
                            <AutocompleteWebsiteWrapper>
                                <AutocompleteWebsitesBase
                                    placeholder={placeholder}
                                    defaultWebsitePageState={websitePageStateOverride ?? defaultWebsitePageState}
                                />
                            </AutocompleteWebsiteWrapper>
                            {popularSitesWithIcon ? renderWebsiteExamples() : null}
                        </HeaderContent>
                    </HomepageInnerWrapper>
                </HomepageHeader>
                <HomepageBody>{renderBody()}</HomepageBody>
                <HomePageBannerWrapper>
                    <AIReportCtaSection placement={AIReportCtaPlacement.ANALYSIS} />
                </HomePageBannerWrapper>
                <HomepagePreviewInnerWrapper>
                    <TitlePagePrevieWrapper>
                        <LineTitle />
                        <TitlePagePreview>{i18nFilter()("growth.websiteanalysis.preview.title")}</TitlePagePreview>
                        <LineTitle />
                    </TitlePagePrevieWrapper>
                    {pagesPreview.map((pagePreview, index) => {
                        return (
                            <PagePreview
                                key={index.toString()}
                                isImageBeforeText={!!(index % 2)}
                                image={pagePreview.image}
                                title={i18nFilter()(pagePreview.title)}
                                descriptions={pagePreview.descriptions.map((description) => i18nFilter()(description))}
                                textButton={i18nFilter()(pagePreview.textButton)}
                                onClickButton={() => {
                                    onClickPagePreviewButton(pagePreview.navigationState, pagePreview.pageName);
                                }}
                            ></PagePreview>
                        );
                    })}
                </HomepagePreviewInnerWrapper>
                <FeedbackSideTab />
                <RewardsSideBarContainer />
            </HomePage>
        </ScrollContainer>
    );
};
